/**
 * Team cryptographic utilities — X25519 key exchange + AES-GCM key wrapping.
 *
 * Used to distribute team encryption keys to team members.
 * Pure JS implementation via @noble/* — works on native and web (no platform split).
 */

import { x25519 } from '@noble/curves/ed25519.js';
import { gcm } from '@noble/ciphers/aes.js';
import { getRandomBytes } from './crypto';

// ── X25519 Key Pair ──────────────────────────────────────────────────────────

/**
 * Derive an X25519 key pair from a 32-byte seed (the PBKDF2 output).
 * X25519 private keys are 32 arbitrary bytes (clamped internally by the library).
 */
export function deriveX25519KeyPair(seed: Uint8Array): {
  privateKey: Uint8Array;
  publicKey: Uint8Array;
} {
  // The seed IS the private key — x25519.getPublicKey handles clamping
  const publicKey = x25519.getPublicKey(seed);
  return { privateKey: seed, publicKey };
}

// ── Team Key Generation ──────────────────────────────────────────────────────

/**
 * Generate a random 256-bit AES key for a team.
 */
export function generateTeamKey(): Uint8Array {
  return getRandomBytes(32);
}

// ── Sealed-Box Key Wrapping ──────────────────────────────────────────────────

/**
 * Wrap (encrypt) a team AES key for a recipient using their X25519 public key.
 *
 * Uses a sealed-box pattern:
 * 1. Generate ephemeral X25519 keypair
 * 2. Compute shared secret via X25519 DH
 * 3. AES-256-GCM encrypt the team key
 *
 * Output format: [ephemeralPublicKey (32) | iv (12) | ciphertext+tag]
 *
 * The wrapper's identity is not revealed (ephemeral key is one-time use).
 */
export function wrapTeamKey(
  teamKey: Uint8Array,
  recipientPublicKey: Uint8Array,
): Uint8Array {
  // Generate ephemeral keypair for this wrap operation
  const ephemeralPrivateKey = getRandomBytes(32);
  const ephemeralPublicKey = x25519.getPublicKey(ephemeralPrivateKey);

  // Derive shared secret via X25519 DH
  const sharedSecret = x25519.getSharedSecret(ephemeralPrivateKey, recipientPublicKey);

  // AES-256-GCM encrypt the team key
  const iv = getRandomBytes(12);
  const aes = gcm(sharedSecret, iv);
  const ciphertext = aes.encrypt(teamKey);

  // Pack: [ephemeralPublicKey (32) | iv (12) | ciphertext+tag]
  const result = new Uint8Array(32 + 12 + ciphertext.length);
  result.set(ephemeralPublicKey, 0);
  result.set(iv, 32);
  result.set(ciphertext, 44);
  return result;
}

/**
 * Unwrap (decrypt) a team AES key using the recipient's X25519 private key.
 *
 * Reverses the sealed-box:
 * 1. Extract ephemeral public key from the blob
 * 2. Compute shared secret via X25519 DH
 * 3. AES-256-GCM decrypt to recover the team key
 */
export function unwrapTeamKey(
  wrappedKey: Uint8Array,
  recipientPrivateKey: Uint8Array,
): Uint8Array {
  // Unpack: [ephemeralPublicKey (32) | iv (12) | ciphertext+tag]
  const ephemeralPublicKey = wrappedKey.slice(0, 32);
  const iv = wrappedKey.slice(32, 44);
  const ciphertext = wrappedKey.slice(44);

  // Derive shared secret via X25519 DH
  const sharedSecret = x25519.getSharedSecret(recipientPrivateKey, ephemeralPublicKey);

  // AES-256-GCM decrypt
  const aes = gcm(sharedSecret, iv);
  return aes.decrypt(ciphertext);
}
